// src/context/OnboardingContext.tsx
import React, {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
} from "react";

export type OnboardingStepId =
    | "goToSources"
    | "project"
    | "baseline"
    | "documents"
    | "analysis"
    | "reviewFlag"
    | "export";

type OnboardingStep = {
    id: OnboardingStepId;
    label: string;
    description: string;
};

type OnboardingState = {
    completed: Record<OnboardingStepId, boolean>;
    slideoverOpened: boolean;
};

type OnboardingContextValue = {
    steps: (OnboardingStep & { completed: boolean })[];
    currentStepId: OnboardingStepId | null;
    isComplete: boolean;
    state: OnboardingState;
    markStepComplete: (id: OnboardingStepId) => void;
    markSlideoverOpened: () => void;
    resetOnboarding: () => void;
};

const STEPS: OnboardingStep[] = [
    {
        id: "project",
        label: "Choose a project",
        description: "Select or create a project to work on.",
    },
    {
        id: "baseline",
        label: "Set baseline scope",
        description: "Upload the agreed scope / contract.",
    },
    {
        id: "documents",
        label: "Add project docs",
        description: "Add emails, notes, and project documents.",
    },
    {
        id: "analysis",
        label: "Run analysis",
        description: "Let ScopeShield scan for scope creep.",
    },
    {
        id: "reviewFlag",
        label: "Review flags",
        description: "Open at least one flag to see details.",
    },
    {
        id: "export",
        label: "Draft ASR",
        description: "Generate an add service draft from a flag.",
    },
];

const STORAGE_KEY = "scopeshield_onboarding_v1";

const defaultState: OnboardingState = {
    completed: {
        goToSources: false,
        project: false,
        baseline: false,
        documents: false,
        analysis: false,
        reviewFlag: false,
        export: false,
    },
    slideoverOpened: false,
};

const OnboardingContext = createContext<OnboardingContextValue | null>(null);

function loadInitialState(): OnboardingState {
    try {
        const raw = window.localStorage.getItem(STORAGE_KEY);
        if (!raw) return defaultState;
        const parsed = JSON.parse(raw);

        // Migrate from old 'completedSteps' to new 'completed' structure
        const completedSteps = parsed.completedSteps ?? parsed.completed ?? {};

        return {
            ...defaultState,
            ...parsed,
            completed: {
                ...defaultState.completed,
                ...completedSteps,
            },
            slideoverOpened: parsed.slideoverOpened ?? parsed.hasOpenedSlideover ?? false,
        };
    } catch {
        return defaultState;
    }
}

export const OnboardingProvider: React.FC<{ children: React.ReactNode }> = ({
    children,
}) => {
    const [state, setState] = useState<OnboardingState>(() => loadInitialState());

    useEffect(() => {
        try {
            window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
        } catch {
            // ignore
        }
    }, [state]);

    const markStepComplete = useCallback((id: OnboardingStepId) => {
        setState((prev) => {
            if (prev.completed[id]) return prev;
            return {
                ...prev,
                completed: { ...prev.completed, [id]: true },
            };
        });
    }, []);

    const markSlideoverOpened = useCallback(() => {
        setState((prev) => {
            if (prev.slideoverOpened) return prev;
            return { ...prev, slideoverOpened: true };
        });
    }, []);

    const resetOnboarding = useCallback(() => {
        setState(defaultState);
    }, []);

    const isComplete = useMemo(
        () => Object.values(state.completed).every(Boolean),
        [state.completed],
    );

    const currentStepId: OnboardingStepId | null = useMemo(() => {
        if (isComplete) return null;
        for (const step of STEPS) {
            if (!state.completed[step.id]) return step.id;
        }
        return null;
    }, [state.completed, isComplete]);

    const stepsWithStatus = useMemo(
        () =>
            STEPS.map((step) => ({
                ...step,
                completed: state.completed[step.id],
            })),
        [state.completed],
    );

    const value: OnboardingContextValue = {
        steps: stepsWithStatus,
        currentStepId,
        isComplete,
        state,
        markStepComplete,
        markSlideoverOpened,
        resetOnboarding,
    };

    return (
        <OnboardingContext.Provider value={value}>
            {children}
        </OnboardingContext.Provider>
    );
};

export function useOnboarding() {
    const ctx = useContext(OnboardingContext);
    if (!ctx) {
        throw new Error("useOnboarding must be used within OnboardingProvider");
    }
    return ctx;
}
